"use client"

import { RootState } from "@/(admin)/modules/auth/services/redux/store"
import {
  addPost,
  approvePost,
  checkSlug,
  checkSlugAvailability,
  createPostLocalization,
  deleteMine,
  deletePost,
  fetchPostId,
  fetchPublishedSlugsByIds,
  fetchPostMineById,
  fetchPosts,
  fetchPostsMine,
  FetchPostsQueryConfig,
  publishPost,
  rejectPost,
  unPublishPost,
  updateMine,
  updatePost
} from "@/(admin)/modules/post/services/post"
import { t } from "@/languages/i18n"
import { normalizeText } from "@/lib/external-components"
import { purgeRemovedPostSlugs } from "@/validate/validatePosts"
import { useDomainStore } from "@/stores/use-domain-store"
import { AttributesPost, CategoryData, FieldType, MediaData, PaginationMeta, PostData, PostLocalizationPayload, TagData } from "@/types/types"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { getLocalISOString } from "@/utils/date-utils"
import { generateAndValidateSlug } from "@/utils/slug-utils"
import { App } from "antd"
import { useRouter } from "next/navigation"
import queryString from "query-string"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"

const DEFAULT_POSTS_QUERY_CONFIG: FetchPostsQueryConfig = {
  fields: ["title", "description", "createdAt", "publishedAt", "locale", "views", "isLiveHappening", "status", "scheduledAt"],
  populate: {
    categories: {
      fields: ["name"],
    },
    author: {
      fields: ["username"],
    },
    tags: {
      fields: ["name"],
    },
  },
}

export type UsePostsScope = "all" | "mine"

/**
 * Chạy các tác vụ TUẦN TỰ (không bắn song song). Bulk update quan hệ nhiều-nhiều
 * (posts_websites_links) hay bulk /publish nếu chạy `Promise.all` sẽ ghi đồng thời
 * → deadlock/contention ở Strapi (500 xen kẽ, không ổn định). Tuần tự thì ổn định
 * và vẫn thu được kết quả từng bài như allSettled.
 */
async function runInSeries<T>(tasks: Array<() => Promise<T>>): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = []
  for (const task of tasks) {
    try {
      results.push({ status: "fulfilled", value: await task() })
    } catch (reason) {
      results.push({ status: "rejected", reason })
    }
  }
  return results
}

export const usePosts = (options?: { scope?: UsePostsScope; fetchPostByIdUseMine?: boolean }) => {
  const scope: UsePostsScope = options?.scope ?? "all"
  const isMineScope = scope === "mine"
  const fetchPostByIdUseMine = options?.fetchPostByIdUseMine ?? false
  const router = useRouter()
  const { message } = App.useApp()
  const account = useSelector((state: RootState) => state.account)
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  const enforcedDomainRef = useRef(enforcedDomain)
  const abortControllerRef = useRef<AbortController | null>(null)
  const fetchPostsQueryConfigRef = useRef<FetchPostsQueryConfig | undefined>(undefined)

  const [dataItem, setDataItem] = useState<PostData[]>([])
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalItem, setTotalItem] = useState(0)
  const [postNameOptions, setPostNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [tagOptions, setTagOptions] = useState<{ value: number; label: string }[]>([])
  const [filterTabPost, setFilterTabPost] = useState("all")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const [postItemData, setPostItemData] = useState<PostData>()
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const { modal } = App.useApp();

  useEffect(() => {
    enforcedDomainRef.current = enforcedDomain
  }, [enforcedDomain])

  const fetchPostsData = useCallback(
    async (
      searchTerms?: { [key: string]: string | number | object },
      queryConfig?: FetchPostsQueryConfig,
    ) => {

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      const resolvedQueryConfig = queryConfig || fetchPostsQueryConfigRef.current || DEFAULT_POSTS_QUERY_CONFIG
      fetchPostsQueryConfigRef.current = resolvedQueryConfig

      setLoading(true)
      setDataItem([])
      try {
        const defaultPagination = {
          page: "1",
          pageSize: "20",
        }
        const currentEnforcedDomain = enforcedDomainRef.current
        const finalSearchTerms = {
          ...defaultPagination,
          ...searchTerms,
          ...(currentEnforcedDomain ? { domain: currentEnforcedDomain } : {}),
        }

        const allResponse = isMineScope
          ? await fetchPostsMine(finalSearchTerms as any, abortControllerRef.current.signal, resolvedQueryConfig)
          : await fetchPosts(finalSearchTerms as any, abortControllerRef.current.signal, resolvedQueryConfig)

        if (!allResponse) return

        const response = allResponse && allResponse.data
        const meta = allResponse && allResponse.meta
        setMetaData(meta)

        if (response && response.length > 0) {
          setDataItem(response)
          if (searchTerms?.status === "all" || !searchTerms?.status) {
            setTotalItem(meta?.pagination?.total || 0)
          }

          const uniqueTags = Array.from(
            new Map<string, { id: number; name: string }>(
              response
                .flatMap((post: { id: number; attributes: AttributesPost }) =>
                  (post.attributes.tags?.data || []).map((tag: TagData) => ({
                    id: tag.id,
                    name: tag.attributes.name,
                  })),
                )
                .map((tag: { id: number; name: string }) => [tag.id, tag] as [number, { id: number; name: string }]),
            ).values(),
          )

          setTagOptions(
            uniqueTags.map((tag: { id: number; name: string }) => ({
              value: tag.id,
              label: tag.name,
            })),
          )
        }
        return response
      } catch (error: any) {
        if (error.name === "AbortError" || error.code === "ERR_CANCELED") return
        message.error(t("Lỗi khi tải danh sách bài viết. Vui lòng thử lại."))
      } finally {
        setLoading(false)
      }
    },
    [message, isMineScope],
  )

  const getPostInformation = useCallback(
    async (id: number) => {
      try {
        const res = fetchPostByIdUseMine ? await fetchPostMineById(id) : await fetchPostId(id)
        setPostItemData(res)
      } catch (error) {
        message.error(t("Lỗi khi tải thông tin bài viết!"))
      }
    },
    [message, fetchPostByIdUseMine],
  )

  const handleDuplicate = useCallback(async () => {
    if (postItemData) {
      const baseSlug = postItemData.attributes.slug
      const baseTitle = postItemData.attributes.title
      const baseDescription = postItemData.attributes.description

      let uniqueSlug = baseSlug
      let uniqueTitle = baseTitle
      let uniqueDescription = baseDescription

      const hasDescription = baseDescription !== null && baseDescription !== undefined

      const copyTitle = `${baseTitle} Copy`
      const generated = await generateAndValidateSlug(copyTitle, checkSlug)
      uniqueSlug = generated.slug
      uniqueTitle = copyTitle
      uniqueDescription = hasDescription ? baseDescription : ""

      const dataPayload = {
        author: account.user,
        title: uniqueTitle,
        slug: uniqueSlug,
        content: postItemData.attributes.content,
        description: hasDescription ? uniqueDescription : null,
        readingTime: postItemData.attributes.readingTime,
        shortDescription: postItemData.attributes.shortDescription,
        categories: postItemData.attributes.categories?.data?.map((cat: CategoryData) => cat.id) || [],
        tags: postItemData.attributes.tags?.data?.map((tag: TagData) => tag.id) || [],
        websites: postItemData.attributes.websites?.data?.map((site: { id: number }) => site.id) || [],
        campaign: postItemData.attributes.campaign?.data?.id ?? null,
        promo_video_urls: postItemData.attributes.promo_video_urls || [],
        // Nhân bản luôn ra bản nháp: bỏ ngày xuất bản gốc để bài copy không bị coi
        // là đã publish.
        publishedAt: null,
        status: "draft",
        image: postItemData.attributes.image?.data?.id || null,
        cover: postItemData.attributes.cover?.data?.id || postItemData.attributes.image?.data?.id || null,
        locale: postItemData.attributes.locale,
        isLiveNews: postItemData.attributes.isLiveNews || false,
        isLiveHappening: postItemData.attributes.isLiveHappening || false,
        liveNewsItem:
          postItemData.attributes.liveNewsItem && postItemData.attributes.liveNewsItem.length > 0
            ? postItemData.attributes.liveNewsItem.map((item: any) => ({
              title: item.title,
              date: item.date,
              detail: item.detail,
            }))
            : [],
        seo: {
          id: 0,
          metaTitle: postItemData.attributes.seo?.metaTitle,
          metaDescription: postItemData.attributes.seo?.metaDescription,
          canonicalURL: postItemData.attributes.seo?.canonicalURL,
          keywords: postItemData.attributes.seo?.keywords,
          metaRobots: postItemData.attributes.seo?.metaRobots,
          metaViewport: postItemData.attributes.seo?.metaViewport,
          structuredData: postItemData.attributes.seo?.structuredData || {},
        },
      }

      try {
        await addPost({ data: dataPayload })
        message.success({ content: t("Nhân bản bài viết thành công!"), duration: 2 })
        setPostItemData(undefined)
        setNeedRefresh(prev => !prev)
      } catch (error) {
        message.error(t("Lỗi khi nhân bản bài viết. Vui lòng thử lại."))
      }
    }
  }, [postItemData, account.user, message, setNeedRefresh])

  const handleDelete = useCallback(
    async (id: number, filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
      modal.confirm({
        title: t("Xác nhận xóa"),
        content: t("Bạn có chắc chắn muốn xóa bài viết này không?"),
        okText: t("Xóa"),
        cancelText: t("Hủy"),
        onOk: async () => {
          try {
            setLoading(true)
            // Chụp slug TRƯỚC khi xoá để purge cứng URL (đã biến mất) → 404 ngay
            // thay vì để stale-watermark serve bản cũ tới hết TTL. Chỉ purge khi
            // đã publish — bản nháp chưa từng public nên không có cache để xoá.
            let deletedSlug = ""
            try {
              const deletedPost = await fetchPostId(id)
              if (deletedPost?.attributes?.publishedAt) deletedSlug = deletedPost?.attributes?.slug ?? ""
            } catch {}
            if (isMineScope) {
              await deleteMine(id)
            } else {
              await deletePost(id)
            }
            if (deletedSlug) await purgeRemovedPostSlugs([deletedSlug])
            const currentParams = {
              locale: "all",
              ...sortPaginationValues,
              status: filterTabPost,
              ...filterValues,
            }
            fetchPostsData(currentParams)
            message.success(t("Xóa bài viết thành công!"))
          } catch {
            message.error(t("Lỗi khi thực hiện xóa bài viết"))
          }
        },
      })
    },
    [message, filterTabPost, fetchPostsData, isMineScope, modal],
  )

  const handleBulkDelete = useCallback(
    async (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
      try {
        setLoading(true)
        const ids = selectedRowKeys.map(id => Number(id))
        // Chụp slug (đã publish) trước khi xoá — 1 query cho cả lô, tránh N+1.
        const deletedSlugs = await fetchPublishedSlugsByIds(ids)
        await Promise.all(ids.map(id => (isMineScope ? deleteMine(id) : deletePost(id))))
        if (deletedSlugs.length) await purgeRemovedPostSlugs(deletedSlugs)
        setSelectedRowKeys([])
        // Refetch theo ĐÚNG bộ lọc hiện tại của List (đã gồm `status` = tab đang xem).
        // Không ép `status: filterTabPost` vì state đó không được List cập nhật, sẽ
        // ghi đè nhầm tab đang xem → list không refresh đúng.
        const currentParams = {
          locale: "all",
          ...filterValues,
          ...sortPaginationValues,
        }
        fetchPostsData(currentParams)
        setLoading(false)
        message.success(t("Xóa các bài viết thành công!"))
      } catch {
        message.error(t("Lỗi khi thực hiện xóa hàng loạt"))
      }
    },
    [selectedRowKeys, message, fetchPostsData, isMineScope],
  )

  const handleBulkStatus = useCallback(
    async (
      status: "publish" | "draft",
      filterValues?: Record<string, string>,
      sortPaginationValues?: Record<string, string>,
    ) => {
      const ids = selectedRowKeys.map(id => Number(id))

      // Pre-filter: loại trước các bài đã ở đúng trạng thái đích để KHÔNG gửi lên
      // /publish|/unpublish (Strapi trả 400 "đã xuất bản" / "chỉ bài đã xuất bản mới
      // hủy được"). Chỉ gọi API cho bài thực sự cần đổi.
      const eligibleIds = ids.filter(id => {
        const attrs = dataItem.find(post => post.id === id)?.attributes
        const isLive = attrs?.status === "published" || attrs?.publishedAt != null
        const isScheduled = attrs?.status === "scheduled"
        // publish: chỉ bài chưa-live. unpublish: chỉ bài đang published hoặc scheduled.
        return status === "publish" ? !isLive : isLive || isScheduled
      })
      const skipped = ids.length - eligibleIds.length

      if (eligibleIds.length === 0) {
        message.info(
          status === "publish"
            ? t("Tất cả bài viết đã chọn đều đã được xuất bản.")
            : t("Không có bài viết nào đang xuất bản để hủy."),
        )
        return
      }

      try {
        setLoading(true)
        // publishedAt/status là LOCKED_FIELDS ở Strapi (update/updateMine strip đi),
        // nên phải gọi endpoint chuyên biệt /publish và /unpublish. Chạy tuần tự
        // (runInSeries) để tránh 500 do ghi đồng thời.
        const runAction = status === "publish" ? publishPost : unPublishPost
        const results = await runInSeries(eligibleIds.map(id => () => runAction(id)))
        const succeeded = results.filter(r => r.status === "fulfilled").length
        const failed = results.length - succeeded

        setSelectedRowKeys([])
        // Refetch theo đúng bộ lọc hiện tại (gồm `status` = tab đang xem) để bài vừa
        // đổi trạng thái biến mất khỏi tab cũ ngay, không phải F5.
        const currentParams = {
          locale: "all",
          ...filterValues,
          ...sortPaginationValues,
        }
        fetchPostsData(currentParams)
        setLoading(false)

        const successLabel =
          status === "publish" ? t("Xuất bản bài viết thành công!") : t("Hủy xuất bản bài viết thành công!")
        const extras = [
          failed > 0 ? `${failed} ${t("lỗi")}` : "",
          skipped > 0 ? `${skipped} ${t("bỏ qua")}` : "",
        ].filter(Boolean)
        const suffix = extras.length ? ` (${extras.join(", ")})` : ""

        if (succeeded === 0) {
          message.error(`${t("Không cập nhật được bài viết nào.")}${suffix}`)
        } else if (extras.length) {
          message.warning(`${successLabel} ${succeeded} ${t("thành công")}${suffix}`)
        } else {
          message.success(successLabel)
        }
      } catch {
        setLoading(false)
        message.error(t("Lỗi khi cập nhật trạng thái hàng loạt"))
      }
    },
    [selectedRowKeys, dataItem, message, fetchPostsData],
  )

  const handleBulkAssignWebsites = useCallback(
    async (
      websiteIds: number[],
      existingByPostId: Record<number, number[]>,
      filterValues?: Record<string, string>,
      sortPaginationValues?: Record<string, string>,
    ) => {
      if (websiteIds.length === 0) {
        message.warning(t("Vui lòng chọn ít nhất một website."))
        return
      }
      const ids = selectedRowKeys.map(id => Number(id))

      // MERGE + pre-filter: chỉ update bài THỰC SỰ có website mới cần thêm; bài đã
      // gán đủ hết rồi thì bỏ qua (không gửi PUT thừa).
      const targets = ids
        .map(id => {
          const existing = existingByPostId[id] ?? []
          const merged = Array.from(new Set([...existing, ...websiteIds]))
          return { id, merged, changed: merged.length !== existing.length }
        })
        .filter(target => target.changed)
      const skipped = ids.length - targets.length

      if (targets.length === 0) {
        message.info(t("Các bài viết đã chọn đều đã được gán những website này."))
        return
      }

      try {
        setLoading(true)
        // Chạy TUẦN TỰ (runInSeries): ghi bảng quan hệ nhiều-nhiều đồng thời gây 500
        // xen kẽ ở Strapi.
        const results = await runInSeries(
          targets.map(target => () => {
            const payload = { data: { websites: target.merged } }
            return isMineScope ? updateMine(target.id, payload) : updatePost(target.id, payload)
          }),
        )
        const succeeded = results.filter(r => r.status === "fulfilled").length
        const failed = results.length - succeeded

        setSelectedRowKeys([])
        const currentParams = {
          locale: "all",
          ...filterValues,
          ...sortPaginationValues,
        }
        fetchPostsData(currentParams)
        setLoading(false)

        const extras = [
          failed > 0 ? `${failed} ${t("lỗi")}` : "",
          skipped > 0 ? `${skipped} ${t("bỏ qua")}` : "",
        ].filter(Boolean)
        const suffix = extras.length ? ` (${extras.join(", ")})` : ""

        if (succeeded === 0) {
          message.error(`${t("Lỗi khi gán website hàng loạt")}${suffix}`)
        } else if (extras.length) {
          message.warning(`${t("Gán website thành công!")} ${succeeded} ${t("thành công")}${suffix}`)
        } else {
          message.success(t("Gán website thành công!"))
        }
      } catch {
        setLoading(false)
        message.error(t("Lỗi khi gán website hàng loạt"))
      }
    },
    [selectedRowKeys, message, fetchPostsData, isMineScope],
  )

  const handleBulkUnassignWebsites = useCallback(
    async (
      websiteIds: number[],
      existingByPostId: Record<number, number[]>,
      filterValues?: Record<string, string>,
      sortPaginationValues?: Record<string, string>,
    ) => {
      if (websiteIds.length === 0) {
        message.warning(t("Vui lòng chọn ít nhất một website."))
        return
      }
      const ids = selectedRowKeys.map(id => Number(id))

      // Set-difference + pre-filter: chỉ update bài THỰC SỰ đang gán website cần gỡ;
      // bài không dính website nào trong danh sách gỡ thì bỏ qua (không PUT thừa).
      const removeSet = new Set(websiteIds)
      const targets = ids
        .map(id => {
          const existing = existingByPostId[id] ?? []
          const remaining = existing.filter(siteId => !removeSet.has(siteId))
          return { id, remaining, changed: remaining.length !== existing.length }
        })
        .filter(target => target.changed)
      const skipped = ids.length - targets.length

      if (targets.length === 0) {
        message.info(t("Các bài viết đã chọn đều không gán những website này."))
        return
      }

      try {
        setLoading(true)
        // Chạy TUẦN TỰ (runInSeries): ghi bảng quan hệ nhiều-nhiều đồng thời gây 500
        // xen kẽ ở Strapi.
        const results = await runInSeries(
          targets.map(target => () => {
            const payload = { data: { websites: target.remaining } }
            return isMineScope ? updateMine(target.id, payload) : updatePost(target.id, payload)
          }),
        )
        const succeeded = results.filter(r => r.status === "fulfilled").length
        const failed = results.length - succeeded

        setSelectedRowKeys([])
        const currentParams = {
          locale: "all",
          ...filterValues,
          ...sortPaginationValues,
        }
        fetchPostsData(currentParams)
        setLoading(false)

        const extras = [
          failed > 0 ? `${failed} ${t("lỗi")}` : "",
          skipped > 0 ? `${skipped} ${t("bỏ qua")}` : "",
        ].filter(Boolean)
        const suffix = extras.length ? ` (${extras.join(", ")})` : ""

        if (succeeded === 0) {
          message.error(`${t("Lỗi khi gỡ website hàng loạt")}${suffix}`)
        } else if (extras.length) {
          message.warning(`${t("Gỡ website thành công!")} ${succeeded} ${t("thành công")}${suffix}`)
        } else {
          message.success(t("Gỡ website thành công!"))
        }
      } catch {
        setLoading(false)
        message.error(t("Lỗi khi gỡ website hàng loạt"))
      }
    },
    [selectedRowKeys, message, fetchPostsData, isMineScope],
  )

  const handleApplyBulkAction = useCallback(
    (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
      if (selectedRowKeys.length === 0) {
        message.warning(t("Vui lòng chọn các bài viết để thực hiện thao tác."))
        return
      }

      const params = [filterValues, sortPaginationValues] as const

      switch (bulkAction) {
        case "delete":
          modal.confirm({
            title: t("Xác nhận xóa"),
            content: t("Bạn có chắc chắn muốn xóa các bài viết đã chọn không?"),
            okText: t("Xóa"),
            cancelText: t("Hủy"),
            onOk: () => handleBulkDelete(...params),
          })
          break
        case "publish":
          handleBulkStatus("publish", ...params)
          break
        case "draft":
          handleBulkStatus("draft", ...params)
          break
        default:
          message.warning(t("Vui lòng chọn một thao tác hàng loạt hợp lệ."))
      }
    },
    [bulkAction, selectedRowKeys, handleBulkDelete, handleBulkStatus],
  )

  const handleEdit = useCallback(
    (id: number) => {
      router.push(`/admin/posts/edit?id=${id}`)
    },
    [router],
  )

  const handleRefresh = useCallback(
    (filterRef?: { current: { filterFunction: () => void } | null }) => {
      if (filterRef?.current) {
        filterRef.current.filterFunction()
      }
      setNeedRefresh(!needRefresh)
    },
    [needRefresh],
  )

  const handleSearch = useCallback(
    (filters: object, pathname: string) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error(t("Giá trị tìm kiếm không hợp lệ"))
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      if (enforcedDomain) {
        newValue = { ...newValue, domain: enforcedDomain }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${pathname}?${searchParams}` : pathname
      router.push(`${newUrl}`)
    },
    [router, message, enforcedDomain],
  )

  const handleTranslatePost = useCallback(
    async (
      values: FieldType,
      language: string,
      post: PostData | undefined,
      selectedCategories: Array<string | number>,
      selectedTags: string[],
      selectedImage: MediaData | null,
      status: string,
      selectedWebsites: number[] = [],
      selectedCampaignId: number | null = null,
    ) => {
      if (!language) {
        message.error(t("Vui lòng chọn một ngôn ngữ để tạo bản dịch"))
        return { success: false }
      }

      if (!post?.id) {
        message.error(t("Không tìm thấy thông tin bài viết gốc"))
        return { success: false }
      }

      if (language === post.attributes.locale) {
        message.error(t("Ngôn ngữ bản dịch không được trùng với ngôn ngữ gốc"))
        return { success: false }
      }

      const existingLocalizations = post.attributes.localizations?.data || []
      const isLocaleExisted = existingLocalizations.some((loc: any) => loc.attributes.locale === language)

      if (isLocaleExisted) {
        message.error(t("Bản dịch cho ngôn ngữ này đã tồn tại"))
        return { success: false }
      }

      // Use the dedicated check-slug endpoint so the uniqueness check is locale-agnostic.
      // The old checkSlug() call used getBrowserLocale() which only searched within the
      // current UI locale, causing false-positives when the source post is in a different locale.
      const slugCheck = await checkSlugAvailability(values.slug || "")
      if (slugCheck?.isAvailable === false) {
        message.error(t("Đường dẫn đã tồn tại. Vui lòng chọn đường dẫn khác"))
        return { success: false, slugConflict: true as const }
      }

      const authorId = typeof account?.user === 'object' && account?.user !== null
        ? account.user.id
        : account?.user;

      const dataPayload: PostLocalizationPayload = {
        title: values.title,
        slug: values.slug || "",
        content: values.content || "",
        description: values.description,
        categories: selectedCategories.map(id => Number(id)),
        tags: selectedTags.map(id => Number(id)),
        websites: selectedWebsites.map(id => Number(id)),
        campaign: selectedCampaignId ?? undefined,
        publishedAt: status === "Draft" ? null : getLocalISOString(),
        image: selectedImage?.id || null,
        cover: selectedImage?.id || null,
        isLiveNews: values.isLiveNews || false,
        isLiveHappening: values.isLiveHappening || false,
        liveNewsItem: values.liveNewsItem?.map((item: any) => ({
          title: item.title,
          date: item.date,
          detail: item.detail,
        })) || [],
        seo: {
          metaTitle: values.metaTitle || values.title,
          metaDescription: values.metaDescription || values.description,
          canonicalURL: values.canonicalURL,
          keywords: values.keywords,
          metaRobots: values.metaRobots,
          metaViewport: values.metaViewport,
          structuredData: typeof values.structuredData === "string"
            ? JSON.parse(values.structuredData || "{}")
            : values.structuredData,
        },
        locale: language,
        author: authorId || undefined,
        status: status === "Draft" ? "draft" : "published",
      }

      try {
        const result = await createPostLocalization(post.id, dataPayload)
        if (result) {
          message.success(t("Tạo bản dịch thành công!"))
          // Endpoint /localizations trả về bản dịch VỪA tạo. Lấy id để redirect
          // sang chính bản dịch mới (nhất quán với flow tạo bài mới). Xử lý cả 2
          // dạng response: bọc { data: { id } } hoặc phẳng { id }.
          const newLocalizationId = (result as any)?.data?.id ?? (result as any)?.id ?? null
          return { success: true, id: newLocalizationId as number | null }
        }
        // HttpClient nuốt lỗi HTTP (4xx/5xx) và trả null — trước đây nhánh này
        // im lặng nên user bấm lưu mà không thấy gì xảy ra, bản dịch không được tạo.
        message.error(t("Không thể tạo bản dịch. Vui lòng kiểm tra đường dẫn hoặc thử lại"))
        return { success: false }
      } catch (error) {
        message.error(t("Không thể tạo bản dịch. Vui lòng thử lại"))
        return { success: false }
      }
    },
    [message, account?.user, t]
  )

  const getFormDataForTranslate = useCallback((response: PostData) => {
    const formData = {
      title: response.attributes.title,
      slug: response.attributes.slug,
      content: response.attributes.content,
      description: response.attributes.description,
      readingTime: response.attributes.readingTime,
      shortDescription: response.attributes.shortDescription,
      isLiveNews: response.attributes.isLiveNews,
      isLiveHappening: response.attributes.isLiveHappening,
      liveNewsItem: response.attributes.liveNewsItem || [],
      metaTitle: response.attributes.seo?.metaTitle,
      metaDescription: response.attributes.seo?.metaDescription,
      keywords: response.attributes.seo?.keywords,
      metaRobots: response.attributes.seo?.metaRobots,
      metaViewport: response.attributes.seo?.metaViewport,
      structuredData: response.attributes.seo?.structuredData,
      canonicalURL: response.attributes.seo?.canonicalURL,
    }

    const selectedCategories = response.attributes.categories?.data
      ? response.attributes.categories.data.map((cat: CategoryData) => cat.id.toString())
      : []

    const selectedTags = response.attributes.tags?.data
      ? response.attributes.tags.data.map((tag: TagData) => tag.id.toString())
      : []

    const selectedImage = response.attributes.image?.data
      ? ({
        id: response.attributes.image.data.id,
        name: response.attributes.image.data.attributes.name,
        alternativeText: "",
        caption: "",
        width: response.attributes.image.data.attributes.width,
        height: response.attributes.image.data.attributes.height,
        formats: response.attributes.image.data.attributes.formats,
        hash: response.attributes.image.data.attributes.hash,
        ext: response.attributes.image.data.attributes.ext,
        mime: response.attributes.image.data.attributes.mime,
        size: response.attributes.image.data.attributes.size,
        url: response.attributes.image.data.attributes.url,
        previewUrl: response.attributes.image.data.attributes.url,
        provider: response.attributes.image.data.attributes.provider,
        provider_metadata: "",
        createdAt: response.attributes.image.data.attributes.createdAt,
        updatedAt: response.attributes.image.data.attributes.updatedAt,
        attributes: response.attributes.image.data.attributes,
      } as MediaData)
      : null

    return {
      formData,
      selectedCategories,
      selectedTags,
      selectedImage,
    }
  }, [])

  useEffect(() => {
    if (postItemData) {
      handleDuplicate()
    }
  }, [postItemData, handleDuplicate])

  const handleApprove = useCallback(async (id: number) => {
    try {
      setLoading(true)
      await approvePost(id)
      message.success(t("Duyệt bài viết thành công!"))
      return { success: true }
    } catch (error) {
      message.error(t("Lỗi khi duyệt bài viết"))
      return { success: false }
    } finally {
      setLoading(false)
    }
  }, [message, t])

  const handleUnpublish = useCallback(async (id: number) => {
    try {
      setLoading(true)
      await unPublishPost(id)
      message.success(t("Đã hủy xuất bản bài viết"))
      return { success: true }
    } catch (error) {
      message.error(t("Lỗi khi hủy xuất bản bài viết"))
      return { success: false }
    } finally {
      setLoading(false)
    }
  }, [message, t])
  
  const handleReject = useCallback(async (id: number, reason: string) => {
    try {
      setLoading(true)
      await rejectPost(id, reason)
      message.success(t("Đã từ chối bài viết"))
      return { success: true }
    } catch (error) {
      message.error(t("Lỗi khi từ chối bài viết"))
      return { success: false }
    } finally {
      setLoading(false)
    }
  }, [message, t])

  return {
    dataItem,
    loading,
    metaData,
    totalItem,
    postNameOptions,
    tagOptions,
    filterTabPost,
    selectedRowKeys,
    bulkAction,
    postItemData,
    needRefresh,

    actions: {
      fetchPostsData,
      getPostInformation,
      handleDuplicate,
      handleDelete,
      handleBulkDelete,
      handleBulkStatus,
      handleBulkAssignWebsites,
      handleBulkUnassignWebsites,
      handleApplyBulkAction,
      handleEdit,
      handleRefresh,
      handleSearch,
      handleTranslatePost,
      getFormDataForTranslate,
      setSelectedRowKeys,
      setBulkAction,
      setFilterTabPost,
      handleApprove,
      handleUnpublish,
      handleReject
    },

    setters: {
      setDataItem,
      setLoading,
      setMetaData,
      setTotalItem,
      setPostNameOptions,
      setTagOptions,
      setSelectedRowKeys,
      setBulkAction,
      setPostItemData,
      setNeedRefresh,
    },
  }
}